import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { SignupForm, LoginForm } from "./components/AuthForms";
import PhotoUploader from "./components/PhotoUploader";
import PhotoTaggingFlow from "./components/PhotoTaggingFlow";
import UserFeed from "./components/UserFeed";
import UserProfile from "./components/UserProfile";
import Navbar from "./components/Navbar";
import LandingPage from "./components/LandingPage";
import PublicAlbumView from "./components/PublicAlbumView";
import AlbumManage from "./components/AlbumManage";
import Settings from "./components/Settings";
import axios from "axios";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  // Check for existing token on load
  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem("authToken");

      if (token) {
        try {
          // Set default auth header
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

          // Verify token validity by fetching user data
          const response = await axios.get(
            `${process.env.REACT_APP_DATABASE_URL}/api/users/me`
          );
          setUser(response.data);
          setIsAuthenticated(true);
        } catch (error) {
          // Token invalid or expired
          localStorage.removeItem("authToken");
          delete axios.defaults.headers.common["Authorization"];
        }
      }

      setLoading(false);
    };

    checkAuthStatus();
  }, []);

  // Authentication handler
  const handleAuthSuccess = (userData, redirectUrl) => {
    setIsAuthenticated(true);
    setUser(userData);
    
    // If there's a redirect URL, navigate to it
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  };

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem("authToken");
    delete axios.defaults.headers.common["Authorization"];
    setIsAuthenticated(false);
    setUser(null);
  };

  // Handle user update (used by settings page)
  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
  };

  // Handle photo upload completion
  const handleUploadComplete = (photos) => {
    setUploadedPhotos(photos);
  };

  // Handle tagging completion
  const handleTaggingComplete = () => {
    // Reset uploaded photos and navigate to feed
    setUploadedPhotos([]);
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-r from-purple-500 to-pink-500">
        <div className="text-white text-2xl font-bold animate-pulse">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <Navbar
          isAuthenticated={isAuthenticated}
          user={user}
          onLogout={handleLogout}
        />

        <main className="pt-16 pb-10">
          <Routes>
            {/* Public routes */}
            <Route
              path="/"
              element={
                isAuthenticated ? <Navigate to="/feed" /> : <LandingPage />
              }
            />
            <Route
              path="/login"
              element={
                isAuthenticated ? (
                  <Navigate to="/feed" />
                ) : (
                  <LoginForm onSuccess={handleAuthSuccess} />
                )
              }
            />
            <Route
              path="/signup"
              element={
                isAuthenticated ? (
                  <Navigate to="/feed" />
                ) : (
                  <SignupForm onSuccess={handleAuthSuccess} />
                )
              }
            />
            
            {/* Public album view route - accessible to everyone */}
            <Route
              path="/s/:shareId"
              element={<PublicAlbumView isAuthenticated={isAuthenticated} user={user} />}
            />
            
            {/* Protected routes */}
            <Route
              path="/upload"
              element={
                isAuthenticated ? (
                  <PhotoUploader onUploadComplete={handleUploadComplete} />
                ) : (
                  <Navigate to="/login" state={{ from: "/upload" }} />
                )
              }
            />
            <Route
              path="/tagging"
              element={
                isAuthenticated && uploadedPhotos.length > 0 ? (
                  <PhotoTaggingFlow
                    uploadedPhotos={uploadedPhotos}
                    onComplete={handleTaggingComplete}
                  />
                ) : (
                  <Navigate to={isAuthenticated ? "/upload" : "/login"} />
                )
              }
            />
            <Route
              path="/feed"
              element={
                isAuthenticated ? (
                  <UserFeed />
                ) : (
                  <Navigate to="/login" state={{ from: "/feed" }} />
                )
              }
            />
            <Route
              path="/profile/:userId"
              element={
                isAuthenticated ? (
                  <UserProfile />
                ) : (
                  <Navigate
                    to="/login"
                    state={{ from: window.location.pathname }}
                  />
                )
              }
            />
            
            {/* Album management route */}
            <Route
              path="/album/edit/:albumId"
              element={
                isAuthenticated ? (
                  <AlbumManage />
                ) : (
                  <Navigate
                    to="/login"
                    state={{ from: window.location.pathname }}
                  />
                )
              }
            />
            
            {/* Settings route */}
            <Route
              path="/settings"
              element={
                isAuthenticated ? (
                  <Settings 
                    user={user} 
                    onUserUpdate={handleUserUpdate} 
                  />
                ) : (
                  <Navigate
                    to="/login"
                    state={{ from: "/settings" }}
                  />
                )
              }
            />
            
            {/* Default redirect */}
            <Route
              path="*"
              element={
                <div className="flex flex-col items-center justify-center h-96">
                  <h1 className="text-4xl font-bold mb-4">404</h1>
                  <p className="text-xl mb-6">Oops! Page not found</p>
                  <button
                    onClick={() => window.history.back()}
                    className="px-4 py-2 bg-purple-600 text-white rounded-full hover:bg-purple-700"
                  >
                    Go Back
                  </button>
                </div>
              }
            />
          </Routes>
        </main>

        {/* Mobile Action Button */}
        {isAuthenticated && (
          <div className="fixed bottom-6 right-6 md:hidden">
            <button
              onClick={() => (window.location.href = "/upload")}
              className="flex items-center justify-center w-14 h-14 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full shadow-lg text-white"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </Router>
  );
}

export default App;